import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { Counter } from './counter';
import { renderIcon, type IconLike } from './icon';
import { tv, type VariantProps } from './lib/tv';

/**
 * Segmented control — kit 67:11756 (spec §4.10).
 *
 * `kit`: track White/5 r8, padding 1, gap 4; segment r7; active = accent-2 (`#35b5ec`) + shadow-xs + semibold,
 * idle = regular white; icons White/60 in both states. Sizes sm 24/22 (3/5, 12/16) · md 32/30 (5/10, 14/20) · lg 36/34 (7/12, 14/20).
 * Product: "Chizma | 3D" (kit sm, 429:15777), "Setka | Mozaika | Lenta" (kit md, full width, 406:9366),
 * modal form segments with `tone="accent"` (#0275ff, 317:7795).
 *
 * `pill`: filter pills (565:23094 "1 KUN · 7 KUN · 30 KUN · Barchasi") — track h32 White/10 r8, padding 2, gap 2;
 * segment h28 r6 padding 0/14, 500 12/17; active accent-2 + white, idle White/60.
 */
export const segmentedStyles = tv({
  slots: {
    track: 'inline-flex max-w-full items-stretch',
    item: [
      'relative inline-flex min-w-0 cursor-pointer items-center justify-center whitespace-nowrap outline-none select-none',
      'transition-[background-color,color,box-shadow] duration-150 ease-standard',
      'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white',
      'data-disabled:cursor-not-allowed data-disabled:opacity-40',
    ],
    label: 'min-w-0 truncate',
    icon: 'shrink-0',
  },
  variants: {
    appearance: {
      kit: {
        track: 'gap-4 rounded-8 bg-subtle p-1',
        item: 'rounded-7 font-normal text-fg not-data-checked:hover:bg-hover data-checked:font-semibold data-checked:shadow-xs',
        label: 'px-4',
        // kit Remix icons render White/60 in both states (351:6386 view toggle: 164,167,175 idle, 154,200,255 on blue)
        icon: 'text-fg-secondary',
      },
      pill: {
        track: 'h-32 gap-2 rounded-8 bg-field p-2',
        item: 'h-28 rounded-6 px-14 text-xs leading-17 font-medium tracking-normal text-fg-secondary not-data-checked:hover:text-fg data-checked:text-on-accent',
      },
    },
    size: { sm: '', md: '', lg: '' },
    tone: {
      accent: { item: 'data-checked:bg-accent data-checked:text-on-accent' },
      accent2: { item: 'data-checked:bg-accent-2 data-checked:text-on-accent' },
    },
    fullWidth: { true: { track: 'flex w-full', item: 'flex-1' } },
  },
  compoundVariants: [
    { appearance: 'kit', size: 'sm', class: { track: 'h-24', item: 'h-22 gap-2 px-5 py-3 text-xs', icon: 'size-16' } },
    { appearance: 'kit', size: 'md', class: { track: 'h-32', item: 'h-30 gap-2 px-10 py-5 text-sm', icon: 'size-16' } },
    { appearance: 'kit', size: 'lg', class: { track: 'h-36', item: 'h-34 gap-2 px-12 py-7 text-sm', icon: 'size-20' } },
    { appearance: 'pill', class: { icon: 'size-14' } },
  ],
  defaultVariants: { appearance: 'kit', size: 'md', tone: 'accent2' },
});

export interface SegmentedItem<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: IconLike;
  count?: number;
  disabled?: boolean;
  /** Required for icon-only segments. */
  'aria-label'?: string;
}

export interface SegmentedControlProps<T extends string> extends VariantProps<typeof segmentedStyles> {
  items: readonly SegmentedItem<T>[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  disabled?: boolean;
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

/** Single-choice segmented control (radio-group semantics, arrow keys move the selection). */
export function SegmentedControl<T extends string>({
  items,
  value,
  defaultValue,
  onValueChange,
  disabled,
  name,
  appearance,
  size,
  tone,
  fullWidth,
  className,
  ...aria
}: SegmentedControlProps<T>) {
  const s = segmentedStyles({ appearance, size, tone, fullWidth });
  return (
    <BaseRadioGroup
      {...aria}
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(next as T)}
      disabled={disabled}
      name={name}
      className={s.track({ class: className })}
    >
      {items.map((item) => (
        <BaseRadio.Root
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          aria-label={item['aria-label']}
          className={s.item()}
        >
          {renderIcon(item.icon, s.icon())}
          {item.label != null && item.label !== '' && <span className={s.label()}>{item.label}</span>}
          {item.count != null && <Counter value={item.count} size="sm" tone="white" />}
        </BaseRadio.Root>
      ))}
    </BaseRadioGroup>
  );
}
